import { Address, assembleAssetTitle } from 'ComponentLibrary/src/common/address'
import { AppraiserAssignment, isoDateStringToLocalFormat, OrderStatus, OrderStatusIcon, tokens, useOrderStatus } from 'ComponentLibrary'
import { Box, Stack, styled } from '@mui/material'
import { Typography } from 'ComponentLibrary/src/piazza'
import { LoadingSkeletonContainer } from 'ComponentLibrary/src/atoms/LoadingSkeleton'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { type AppraiserAssignment as AppraiserAssignmentType, User } from 'ComponentLibrary/src/common/users'
import { DateString } from 'ComponentLibrary/src/common/date'
import { type AppraiserAssignmentCopy } from 'ComponentLibrary/src/widgets/AppraiserAssignment/AppraiserAssignment'
import { type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'

type AssetDetails = {
    assetAddress: Address & { assetTitle?: string; customer_asset_reference?: string }
    onAssetLinkClicked: () => void
}
type OrderDetailsProps = {
    orderAddress: Address
    onOrderLinkClicked: () => void
    valuationDate?: DateString
    status: OrderStatus
    appraiserAssignment?: AppraiserAssignmentType
    appraiserUsers: User[]
    assetDetails?: AssetDetails
    shouldIncludeAsset: boolean // on ClientPortal, only assets with finalised valuation will be shown
    portfolios?: string[]
    copy?: Partial<OrderDetailsCopy>
    userLinkCopy?: Partial<UserLinkCopy>
    locale?: string
}

export type OrderDetailsCopy = {
    title: string
    orderName: string
    valuationDate: string
    orderStatus: string
    appraiser: string
    asset: string
    portfolio: string
    portfolios: string
    appraiserAssignment: AppraiserAssignmentCopy
    orderStatusLabel?: (status: OrderStatus) => string
}

const DEFAULT_ORDER_DETAILS_COPY: OrderDetailsCopy = {
    title: 'Auftragsdaten',
    orderName: 'Auftragsname',
    valuationDate: 'Wertermittlungsstichtag',
    orderStatus: 'Auftragsstatus',
    appraiser: 'Bewerter/in',
    asset: 'Immobilie',
    portfolio: 'Portfolio',
    portfolios: 'Portfolios',
    appraiserAssignment: {
        addButton: 'Gutachter/in hinzufügen',
        modal: {
            customOptionName: '► Extern (Freitext)',
            customOptionGroup: 'Nicht registrierte Gutachter',
            title: 'Zuständige Gutachter/innen',
            cancel: 'Abbrechen',
            duplicateWarning: 'Derselbe Gutachter wurde mehrfach ausgewählt. Bitte ändern Sie einen der Einträge.',
            customInfo:
                'Freitext bitte nur für nicht registrierte Gutachter verwenden. Automatisierungen funktionieren nur mit verknüpften Gutachtern.',
            mainAppraiser: 'Erstgutachter/in',
            secondaryAppraiser: 'Zweitgutachter/in',
            tertiaryAppraiser: 'Drittgutachter/in',
            nameAndCertification: 'Name und Zertifizierung',
            remove: 'Entfernen',
            addSecondary: 'Zweitgutachter/in hinzufügen',
            addTertiary: 'Drittgutachter/in hinzufügen',
        },
    },
}

export function OrderDetails({
    orderAddress,
    onOrderLinkClicked,
    valuationDate,
    status,
    appraiserAssignment,
    appraiserUsers,
    assetDetails,
    shouldIncludeAsset,
    portfolios = [],
    copy,
    userLinkCopy,
    locale = 'de-DE',
}: OrderDetailsProps) {
    const { getOrderStatusText } = useOrderStatus()
    const resolvedCopy = { ...DEFAULT_ORDER_DETAILS_COPY, ...copy }
    const appraiserAssignmentCopy = { ...DEFAULT_ORDER_DETAILS_COPY.appraiserAssignment, ...copy?.appraiserAssignment }
    const statusLabel = resolvedCopy.orderStatusLabel?.(status) ?? getOrderStatusText(status)

    const portfoliosString = portfolios.join(', ')
    return (
        <Stack padding={`${tokens.SizingSpacing200} ${tokens.SizingSpacing300}`} height="100%" boxSizing="border-box">
            <Typography variant="headline3" color={tokens.ColorTextBoldest}>
                {resolvedCopy.title}
            </Typography>
            <Stack paddingTop={tokens.SizingSpacing200} gap={tokens.SizingSpacing050}>
                <Typography variant="textS" color={tokens.ColorTextSubtle}>
                    {resolvedCopy.orderName}
                </Typography>
                <Typography variant="link" lineClamp={2} color={tokens.ColorTextLinkDefault} breakLongWords>
                    <LinkBox onClick={onOrderLinkClicked}>{assembleAssetTitle(orderAddress)}</LinkBox>
                </Typography>
            </Stack>
            {valuationDate && (
                <Stack paddingTop={tokens.SizingSpacing200} gap={tokens.SizingSpacing050}>
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {resolvedCopy.valuationDate}
                    </Typography>
                    <Typography variant="textS" color={tokens.ColorTextBold}>
                        {isoDateStringToLocalFormat(valuationDate, false, locale)}
                    </Typography>
                </Stack>
            )}
            <Stack paddingTop={tokens.SizingSpacing200} gap={tokens.SizingSpacing050}>
                <Typography variant="textS" color={tokens.ColorTextSubtle}>
                    {resolvedCopy.orderStatus}
                </Typography>
                <Typography variant="link" color={tokens.ColorTextLinkDefault}>
                    <Stack direction="row" gap={tokens.SizingSpacing100}>
                        <OrderStatusIcon state={status} variant="square" label={statusLabel} />
                        <Typography variant="textS" color={tokens.ColorTextBold}>
                            {statusLabel}
                        </Typography>
                    </Stack>
                </Typography>
            </Stack>
            {appraiserAssignment && (
                <Stack paddingTop={tokens.SizingSpacing200} gap={tokens.SizingSpacing050}>
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {resolvedCopy.appraiser}
                    </Typography>
                    <AppraiserAssignment
                        appraiserAssignment={appraiserAssignment}
                        users={appraiserUsers}
                        copy={appraiserAssignmentCopy}
                        userLinkCopy={userLinkCopy}
                    />
                </Stack>
            )}
            {shouldIncludeAsset && !assetDetails && (
                <Stack paddingTop={tokens.SizingSpacing200}>
                    <LoadingSkeletonContainer loadersPattern={[{ count: 1, height: '60px' }]} />
                </Stack>
            )}
            {assetDetails && (
                <Stack paddingTop={tokens.SizingSpacing200} gap={tokens.SizingSpacing050}>
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {resolvedCopy.asset}
                    </Typography>
                    <Typography variant="link" lineClamp={2} color={tokens.ColorTextLinkDefault} breakLongWords>
                        <LinkBox onClick={assetDetails.onAssetLinkClicked}>{assembleAssetTitle(assetDetails.assetAddress)}</LinkBox>
                    </Typography>
                </Stack>
            )}
            {portfolios?.length !== 0 && (
                <Stack paddingTop={tokens.SizingSpacing200} gap={tokens.SizingSpacing050}>
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {portfolios.length === 1 ? resolvedCopy.portfolio : resolvedCopy.portfolios}
                    </Typography>
                    <TextWithEllipsis
                        tooltipText={portfoliosString}
                        typographyProps={{
                            variant: 'textS',
                            lineClamp: 4,
                            color: tokens.ColorTextBold,
                            breakLongWords: true,
                        }}
                    >
                        {portfoliosString}
                    </TextWithEllipsis>
                </Stack>
            )}
        </Stack>
    )
}

const LinkBox = styled(Box)(() => ({
    cursor: 'pointer',
    color: tokens.ColorTextLinkDefault,
}))
